// Home — employee portal dashboard (design bundle: home.jsx HomePage,
// "employee" view mode).
//
// Two-column grid: a fixed 401px left rail (My access donut, Frequent Access,
// Access Expiring Soon) and a flexible right column holding the Access Catalogs
// grid. Collapses to a single column on narrow viewports.
//
// Mapping notes:
//   • Right column shows Active Luna Requests (only when there ARE active
//     requests — it self-hides otherwise, no empty state) above the Access
//     Catalogs grid. The manager Tasks approval queue is omitted — it's an admin
//     concept with no members endpoint.
//   • Frequent Access has no members API yet → its card shows the design's empty
//     state (see FrequentAccessCard).
//   • Catalog cards use the coverflow CatalogCard (ported from ploy/client) per
//     the user's request, not the bundle's flat `.catalog-card`.
//   • Every card except Luna requests is a doorway to a nav destination, so the
//     org's Portal Navigation selection narrows this page too — see `homeCards`.

import { Box, Grid, GridItem, Stack } from "@chakra-ui/react";
import { Suspense } from "react";
import { TbLayoutDashboard } from "react-icons/tb";
import { useNavigate } from "react-router-dom";

import ErrorWrapper from "components/ErrorWrapper";
import Card from "components/ds/Card";
import EmptyState from "components/ds/EmptyState";
import { useVisibleNavIds } from "components/nav/navConfig";
import { MF_ACCESS } from "state/members/access_v2/catalogs";
import { MF_ACCESS_REVIEWS } from "state/members/accessReviews";
import { MF_TASKS } from "state/members/tasks";
import { useGetLunaRequests } from "state/luna";
import AccessCatalogsCard from "./AccessCatalogsCard";
import { catalogDetailPath } from "./catalogPaths";
import { homeCardVisibility, homeIsEmpty, homeRails } from "./homeCards";
import AccessExpiringCard from "./AccessExpiringCard";
import AccessReviewsCard, {
  CAMPAIGN_FILTERS,
  activeCampaigns,
} from "./AccessReviewsCard";
import FrequentAccessCard from "./FrequentAccessCard";
import LunaRequestsCard, { activeLunaRequests } from "./LunaRequestsCard";
import MyAccessCard from "./MyAccessCard";
import TasksCard from "./TasksCard";

// design ref: styles.css:657–661 — .home-grid (401px 1fr, gap 24px).
const GRID_COLUMNS = { base: "1fr", lg: "401px 1fr" };

// ---- Home -------------------------------------------------------------------
//
// Loading is handled by the route-level <Suspense> in Members/index.tsx — the
// access/catalog queries suspend, so by the time Home renders its data is
// already present. No in-component loading state needed.

const Home = () => {
  const navigate = useNavigate();
  const { data: access } = MF_ACCESS.useListManagedAccess();
  const catalogs = MF_ACCESS.useListAccessCatalogs();
  const { data: tasks } = MF_TASKS.useListTasks();
  const { data: lunaRequests } = useGetLunaRequests();
  const cards = homeCardVisibility(useVisibleNavIds());

  // Same query key and filters as the review queue's own call, so this is the
  // card's fetch, observed a second time rather than repeated. Home only reads
  // the count, but it has to opt out of the error boundary the card gets from
  // its <ErrorWrapper>: a throw up here would blank all of Home instead.
  const { data: campaigns } = MF_ACCESS_REVIEWS.useListCampaigns(
    CAMPAIGN_FILTERS,
    { useErrorBoundary: false },
  );

  // managed-access only returns active grants today, but guard anyway.
  const active = access.filter((a) => a.status !== "expired");

  const goMyAccess = () => navigate("/members/my-access");
  const goCatalog = () => navigate("/members/catalog");

  const rails = homeRails(cards, {
    taskCount: tasks.length,
    activeLunaCount: activeLunaRequests(lunaRequests ?? []).length,
    activeCampaignCount: activeCampaigns(campaigns).length,
  });

  return (
    <Box maxW="1440px" mx="auto" w="full">
      <Grid
        templateColumns={rails.left && rails.right ? GRID_COLUMNS : "1fr"}
        gap="1xl"
        alignItems="start"
      >
        {/* design ref: home.jsx .home-left (gap 24px) */}
        {rails.left && (
          <GridItem minW={0}>
            <Stack spacing="1xl">
              {cards.myAccess && (
                <MyAccessCard
                  access={active}
                  onViewAll={goMyAccess}
                  onBrowseCatalog={cards.browseCatalog ? goCatalog : undefined}
                />
              )}
              {/* Local Suspense + ErrorWrapper: FrequentAccessCard reads
                  my_active_requests (a suspense query Home doesn't otherwise
                  load), so isolate it — it must never block or blank the rest
                  of Home. */}
              {cards.frequentAccess && (
                <ErrorWrapper showToast={false}>
                  <Suspense fallback={null}>
                    <FrequentAccessCard />
                  </Suspense>
                </ErrorWrapper>
              )}
              {cards.accessExpiring && (
                <AccessExpiringCard
                  access={active}
                  onViewAll={goMyAccess}
                  onRowClick={goMyAccess}
                />
              )}
            </Stack>
          </GridItem>
        )}

        {/* design ref: home.jsx .home-right — Active Luna Requests, then Tasks
            (each shown only when non-empty), above the Access Catalogs grid */}
        <GridItem minW={0}>
          <Stack spacing="1xl">
            <ErrorWrapper showToast={false}>
              <LunaRequestsCard
                requests={lunaRequests ?? []}
                onViewAll={() => navigate("/luna/requests")}
                onRespond={(sessionId) =>
                  navigate(`/luna/requests/${sessionId}`)
                }
              />
            </ErrorWrapper>
            {cards.tasks && (
              <ErrorWrapper showToast={false}>
                <TasksCard
                  tasks={tasks}
                  onViewAll={() => navigate("/members/assigned-tasks")}
                />
              </ErrorWrapper>
            )}
            {cards.accessReviews && (
              <ErrorWrapper showToast={false}>
                <AccessReviewsCard
                  onViewAll={() => navigate("/members/access-reviews")}
                  onOpenCampaign={(id) =>
                    navigate(`/members/access-reviews/campaigns/${id}`)
                  }
                />
              </ErrorWrapper>
            )}
            {cards.accessCatalogs && (
              <ErrorWrapper showToast={false}>
                <AccessCatalogsCard
                  catalogs={catalogs}
                  onViewAll={goCatalog}
                  onOpenCatalog={(catalog) =>
                    navigate(catalogDetailPath(catalog.id))
                  }
                />
              </ErrorWrapper>
            )}
            {/* Nothing above rendered: the org switched off every destination
                Home summarises, and the cards that ignore that setting have no
                data either. Say so rather than serving a blank page. */}
            {homeIsEmpty(rails) && (
              <Card>
                <EmptyState
                  icon={<TbLayoutDashboard />}
                  title="Nothing on your dashboard"
                  body="Your IT team has set this portal up to show only what your role needs. Anything that needs your attention will appear here."
                />
              </Card>
            )}
          </Stack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default Home;
